/*
 * Heavily adapted version of simple-form-manager
 * https://www.npmjs.com/package/simple-form-manager
 *
 */

import _ from 'lodash'


export default function FormManager(formElem, formSchema = {}) {
    const buildFieldNameArray = (schema) => {
        return Object.keys(schema).reduce((arr, fieldName) => {
            if (fieldName) arr.push(fieldName);
            return arr;
        }, []);
    };

    const coerce = (value, type) => {
        if (type === Boolean) value = (value && !['false', 'False', '0'].includes(value));
        if (type === Number) value = Number(value) || 0;
        if (type === String) value = String(value) || '';

        // Match WTForm validation for `select` elements
        if (value == 'None' || value == '__None') {
            if (type === Boolean) value = false;
            if (type === Number) value = 0;
            if (type === String) value = '';
        }

        return value;
    };

    const initializeFields = (fieldArray) => {
        let fields = fieldArray.reduce((obj, fieldName) => {
            if (fieldName) {
                obj[fieldName] = {
                    isDirty: false,
                    touched: false,
                    value: null,
                    inputValue: '',
                    originalValue: '',
                    isValid: true,
                    errorMessage: ''
                };
            }
            return obj;
        }, {});

        return fields;
    };

    let fieldNameArray = buildFieldNameArray(formSchema);

    this.form = {
        isDirty: false,
        isValid: true,
        schema: formSchema,
        validateSchema: () => {
            fieldNameArray.forEach(fieldName => {
                if (fieldName) {
                    const schema = this.form.schema;

                    // Provides (some) casting, perhaps type checking in future
                    schema[fieldName].type = schema[fieldName].type || String;

                    // Stores all validators
                    schema[fieldName].validators = schema[fieldName].validators || [];

                    // Tracks dependant fields from requiredIf, etc
                    if (!schema[fieldName].dependants) schema[fieldName].dependants = new Set();

                    schema[fieldName].validators.forEach(v => {
                        // Ignore entries without an actual validator
                        if (v.validator) {
                            if (v.isActive === undefined) v.isActive = true;

                            if (!_.isFunction(v.validator)) {
                                // If not a function, a validator should return
                                // a function and arg mapping
                                let { fn, dependsOn } = v.validator;
                                v.validator = fn;

                                dependsOn.forEach(otherFieldName => {
                                    if (
                                        fieldNameArray.includes(otherFieldName) &&
                                        // Don't create circular dependencies!
                                        fieldName != otherFieldName &&
                                        // Don't create (circular) bidirectional relationships!
                                        !schema[fieldName].dependants.has(otherFieldName)
                                    ) {
                                        if (!schema[otherFieldName].dependants) schema[otherFieldName].dependants = new Set();
                                        schema[otherFieldName].dependants.add(fieldName);
                                    }
                                });
                            }
                        }
                    });
                }
            });
        },
    };

    this.form.validateSchema();
    this.fields = initializeFields(fieldNameArray);

    this.updateData = (data) => {
        const fieldName = data?.name;

        if (fieldName) {
            if (!this.form.schema[fieldName]) return console.error(`Missing schema for field "${fieldName}"`);

            const fn = this.form.schema[fieldName].coerce || coerce;
            const type = this.form.schema[fieldName].type || String;

            if ('value' in data) {
                // Pre-coercion
                data.inputValue = data.value;
                // Post-coercion
                data.value = fn(data.value, type);
            }
            if ('originalValue' in data) data.originalValue = fn(data.originalValue, type);

            this.fields[fieldName] = {
                ...this.fields[fieldName],
                ...data
            };
            this.validateField(fieldName, true);
        }
    };

    this.validateForm = (initialIfDataIsBlank = false) => {
        fieldNameArray.forEach(fieldName => {
            if (this.fields[fieldName].value === null && initialIfDataIsBlank) {
                this.fields[fieldName].value = '';
                this.fields[fieldName].inputValue = '';
                this.fields[fieldName].originalValue = '';
            }
            this.validateField(fieldName, false);
        });

        this.updateFormStatus();
    };

    this.updateFormStatus = () => {
        let isValid = true;
        let isDirty = false;

        fieldNameArray.forEach(fieldName => {
            if (!this.fields[fieldName].isValid) {
                isValid = false;
            }
            if (this.fields[fieldName].isDirty) {
                isDirty = true;
            }
        });
        this.form.isValid = isValid;
        this.form.isDirty = isDirty;
    };

    this.setFormSchema = (formSchema) => {
        this.form.schema = formSchema;
    };

    this.setFieldValidationStatus = (fieldName, value, errorMessage, overrideIfSame = false) => {
        const isSame = (this.fields[fieldName].isValid === value);

        if ((!isSame || (isSame && overrideIfSame)) && this.fields[fieldName].isValid) {
            this.fields[fieldName].isValid = value;
            this.fields[fieldName].errorMessage = errorMessage;
            this.updateFormStatus();
        }
    };

    this.validateField = (fieldName, updateFormStatus = true) => {
        let isValid = true;
        let stopValidation = false;
        let errorMessage = '';

        if (!this.form.schema[fieldName]) return console.error(`Missing schema for field "${fieldName}"`);

        this.form.schema[fieldName].validators.forEach(v => {
            // Ignore entries without validators
            if (v.validator && !stopValidation) {
                if (v.isActive) {
                    const result = v.validator(
                        // Current value, pre-coercion
                        this.fields[fieldName].inputValue,
                        // Full field list for "required if" comparisons
                        this.fields,
                        // Coercion function for this field
                        this.form.schema[fieldName].coerce || coerce,
                        // Data type for this field
                        this.form.schema[fieldName].type || String,
                        // Form DOM element
                        formElem
                    );

                    // Validators can choose to break the chain and return
                    if (result == 'StopValidation') stopValidation = true;

                    if (!result) {
                        isValid = false;
                        if (v.errorMessage) errorMessage = v.errorMessage;
                    }
                }
            }
        });

        // Also fire validators for any dependants
        this.form.schema[fieldName].dependants.forEach(otherFieldName => {
            formElem.querySelector(`[name=${otherFieldName}]`).dispatchEvent(new Event('change'));
        });

        this.fields[fieldName].isValid = isValid;
        this.fields[fieldName].errorMessage = errorMessage;

        if (updateFormStatus) {
            this.updateFormStatus();
        }
    };

    this.toggleValidationNode = (fieldName, validator = undefined, value = undefined) => {
        this.form.schema[fieldName].validators.forEach(v => {
            if (v === validator || !validator) {
                v.isActive = (value === undefined) ? !v.isActive : value;
            }
        });

        this.validateField(fieldName, true);
    };

    this.reset = () => {
        fieldNameArray.forEach(fieldName => {
            if (fieldName) {
                this.fields[fieldName].isDirty = false;
                this.fields[fieldName].touched = false;
                this.fields[fieldName].value = this.fields[fieldName].originalValue;
                this.fields[fieldName].inputValue = this.fields[fieldName].originalValue;
            }
        });
        this.form.isDirty = false;
        this.form.touched = false;
    };

    this.setToClean = () => {
        fieldNameArray.forEach(fieldName => {
            if (fieldName) {
                this.fields[fieldName].isDirty = false;
                this.fields[fieldName].touched = false;
                this.fields[fieldName].originalValue = this.fields[fieldName].value;
                this.fields[fieldName].inputValue = this.fields[fieldName].value;
            }
        });
        this.form.isDirty = false;
        this.form.touched = false;
    };
};
